import { BaseService } from "./BaseService";
import { BuildxObject, ErrorResponse, SuccessResponse, BuildxConfig } from "../types/index";

/**
 * Buildx Objects service for Buildx
 * Handles Buildx-specific objects (users, roles, etc.)
 * 
 * @example
 * ```typescript
 * const buildxObjects = buildx.buildxObjects();
 * 
 * // Get Buildx collection
 * const collection = await buildxObjects.getCollection('users');
 * 
 * // Get Buildx document
 * const doc = await buildxObjects.getDocument('users', 'user-id');
 * 
 * // Query Buildx objects
 * const objects = await buildxObjects.query('users', { limit: 10 });
 * ```
 */
export class BuildxObjects {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * Get Buildx collection
	 */
	async getCollection(type: string, projectId?: string): Promise<any | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/buildx/collection/${type}`);
		return await this.baseService.get(url);
	}

	/**
	 * Get Buildx document by ID
	 */
	async getDocument(type: string, id: string, projectId?: string): Promise<BuildxObject | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/buildx/${type}/${id}`);
		const result = await this.baseService.get(url);

		if (typeof result === "object" && result !== null) {
			result._display = result._display || result.label || result.title || result.name || result.username || result.description || "";
		}

		return result;
	}

	/**
	 * Query Buildx objects
	 */
	async query(type: string, options?: any, projectId?: string): Promise<BuildxObject[] | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/buildx/${type}`);
		const queryParams: Record<string, any> = {};

		if (options) {
			if (options.filter) {
				queryParams.filter = options.filter;
			}
			if (options.jsonFilter) {
				queryParams.jsonFilter = options.jsonFilter;
			}
			if (options.select) {
				queryParams.select = Array.isArray(options.select)
					? options.select.join(",")
					: options.select;
			}
			if (options.projection) {
				queryParams.projection = options.projection;
			}
			if (options.options) {
				queryParams.options = options.options;
			}
			if (options.sort) {
				queryParams.sort = options.sort;
			}
			if (options.limit) {
				queryParams.limit = options.limit;
			}
		}

		const queryString = this.baseService.buildQueryString(queryParams);
		const fullUrl = queryString ? `${url}${queryString}` : url;

		const result = await this.baseService.get(fullUrl);

		if (Array.isArray(result)) {
			result.forEach(row => {
				row._display = row._display || row.label || row.title || row.name || row.username || row.description || "";
			});
		}

		return result;
	}

	/**
	 * Create Buildx object
	 */
	async create(type: string, data: any, projectId?: string): Promise<BuildxObject | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/buildx/${type}`);
		return await this.baseService.post(url, data);
	}

	/**
	 * Update Buildx object
	 */
	async update(type: string, id: string, data: any, projectId?: string): Promise<BuildxObject | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/buildx/${type}/${id}`);
		return await this.baseService.patch(url, data);
	}

	/**
	 * Delete Buildx object
	 */
	async deleteObject(type: string, id: string, projectId?: string): Promise<SuccessResponse | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/buildx/${type}/${id}`);
		return await this.baseService.delete(url);
	}
} 